import { memo, useMemo } from "react";
import {
  EuiTableRowCell,
  EuiPopover,
  EuiContextMenu,
} from "@inscopix/ideas-eui";
import { ProjectFolderDjango } from "pages/gdt/folders/ProjectFoldersProvider.api";
import { useProjectTablesTableContext } from "components/ProjectTablesTable/provider/ProjectTablesTableProvider";
import { ProjectTablesTableColumn } from "components/ProjectTablesTable/ProjectTablesTable.types";
import { ButtonIconPermissioned } from "components/ButtonIconPermissioned/ButtonIconPermissioned";

type ProjectTablesFolderActionMenuProps = {
  column: ProjectTablesTableColumn;
  folder: ProjectFolderDjango;
};

/*
 * Action menu popover for folder rows of the project tables table
 * Allows for:
 * - Invoking folder edit modal
 * - Add a new table to this folder
 * - Invoking folder delete modal
 */
export const ProjectTablesFolderActionMenu = memo(
  function ProjectTablesFolderActionMenu({
    column,
    folder,
  }: ProjectTablesFolderActionMenuProps) {
    const openEditFolderModal = useProjectTablesTableContext(
      (s) => s.openEditFolderModal,
    );
    const openCreateDataTableModal = useProjectTablesTableContext(
      (s) => s.openCreateDataTableModal,
    );
    const openCreateAnalysisTableModal = useProjectTablesTableContext(
      (s) => s.openCreateAnalysisTableModal,
    );
    const openDeleteFolderModal = useProjectTablesTableContext(
      (s) => s.openDeleteFolderModal,
    );
    const togglePopover = useProjectTablesTableContext((s) => s.togglePopover);
    const closePopover = useProjectTablesTableContext((s) => s.closePopover);

    const popoverId = useProjectTablesTableContext((state) => state.popoverId);
    // avoiding EuiIcon heavy rerenders
    const button = useMemo(
      () => (
        <ButtonIconPermissioned
          aria-label="Folder Actions"
          data-test-subj="folder-actions-button"
          iconType="gear"
          size="s"
          color="text"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            togglePopover(folder.id);
          }}
          requiredPermission="edit"
        />
      ),
      [folder.id, togglePopover],
    );
    const contextPanels = [
      {
        id: 0,
        width: 150,
        items: [
          {
            name: "Edit",
            icon: "pencil",
            onClick: () => {
              openEditFolderModal(folder);
              closePopover();
            },
          },
          {
            name: "Add table",
            icon: "plus",
            panel: 1,
          },
          {
            name: "Delete",
            icon: "trash",
            onClick: () => {
              closePopover();
              openDeleteFolderModal(folder);
            },
          },
        ],
      },
      {
        id: 1,
        width: 150,
        title: "Add table",
        items: [
          {
            name: "Data table",
            icon: "tableDensityNormal",
            onClick: () => {
              closePopover();
              openCreateDataTableModal(folder);
            },
          },
          {
            name: "Analysis table",
            icon: "indexMapping",
            onClick: () => {
              closePopover();
              openCreateAnalysisTableModal(folder);
            },
          },
        ],
      },
    ];
    return (
      <EuiTableRowCell
        key={column.id}
        textOnly={false}
        hasActions={true}
        align="right"
      >
        <EuiPopover
          id={`${folder.id}-actions`}
          button={button}
          isOpen={folder.id === popoverId}
          closePopover={() => closePopover()}
          panelPaddingSize="none"
          anchorPosition="leftCenter"
        >
          <EuiContextMenu initialPanelId={0} panels={contextPanels} />
        </EuiPopover>
      </EuiTableRowCell>
    );
  },
);
